import type { PostHog } from "posthog-js"
import type PostHogReactNative from "posthog-react-native"

import type { CaptureExceptionPayload, IdentifyPayload, TrackerAdapter, TrackPayload } from "./base"

export const POSTHOG_SAMPLE_RATE = 0.05

const normalizeSampleRate = (sampleRate: number): number => {
  if (!Number.isFinite(sampleRate)) return 1

  return Math.min(Math.max(sampleRate, 0), 1)
}

export const createPostHogBeforeSend = (
  sampleRate = POSTHOG_SAMPLE_RATE,
  sampleRandom: () => number = Math.random,
) => {
  const normalizedSampleRate = normalizeSampleRate(sampleRate)
  const isSampled =
    normalizedSampleRate >= 1 || (normalizedSampleRate > 0 && sampleRandom() < normalizedSampleRate)

  return <T>(event: T): T | null => (isSampled ? event : null)
}

export interface PostHogAdapterConfig {
  instance: PostHog | PostHogReactNative
  enabled?: boolean
}

export class PostHogAdapter implements TrackerAdapter {
  private posthogInstance: PostHog | PostHogReactNative
  private enabled: boolean

  constructor(config: PostHogAdapterConfig) {
    this.posthogInstance = config.instance
    this.enabled = config.enabled ?? true
  }

  initialize(): void {
    // PostHog is initialized when the instance is passed
  }

  async track({ eventName, properties }: TrackPayload): Promise<void> {
    if (!this.isEnabled()) return

    try {
      this.posthogInstance.capture(eventName, properties as any)
    } catch (error) {
      console.error(`[PostHog] Failed to track event "${eventName}":`, error)
    }
  }

  async captureException({ error, properties }: CaptureExceptionPayload): Promise<void> {
    if (!this.isEnabled()) return

    try {
      this.posthogInstance.captureException(
        error,
        properties as Parameters<typeof this.posthogInstance.captureException>[1],
      )
    } catch (captureError) {
      console.error("[PostHog] Failed to capture exception:", captureError)
    }
  }

  async identify(payload: IdentifyPayload): Promise<void> {
    if (!this.isEnabled()) return

    try {
      this.posthogInstance.identify(payload.id, {
        email: payload.email!,
        name: payload.name!,
        avatar: payload.image!,
        handle: payload.handle!,
      })
    } catch (error) {
      console.error("[PostHog] Failed to identify user:", error)
    }
  }

  async setUserProperties(properties: Record<string, unknown>): Promise<void> {
    if (!this.isEnabled()) return

    try {
      if ("setPersonProperties" in this.posthogInstance) {
        this.posthogInstance.setPersonProperties(properties as any)
      } else {
        ;(this.posthogInstance as PostHogReactNative).setPersonPropertiesForFlags(properties as any)
      }
    } catch (error) {
      console.error("[PostHog] Failed to set user properties:", error)
    }
  }

  async clear(): Promise<void> {
    if (!this.isEnabled()) return

    try {
      this.posthogInstance.reset()
    } catch (error) {
      console.error("[PostHog] Failed to clear user data:", error)
    }
  }

  getName(): string {
    return "PostHog"
  }

  isEnabled(): boolean {
    return this.enabled
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled
  }
}
